import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  SoundOutlined,
  VideoCameraOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import {
  type MediaFormScalars,
  buildMediaPatchFromForm,
  createMedia,
  deleteMedia,
  listMedia,
  parseMediaForEntry,
  resolveMediaAssetUrl,
  updateMedia,
  uploadMediaAudio,
  uploadMediaPhoto,
  uploadMediaVideo,
  type DictionaryMedia,
} from '../api/mediaApi';

type Variant = 'subanta' | 'tinganta';

export type EntryMediaPanelProps = {
  variant: Variant;
  parentId: number;
  rawMedia: unknown;
  onReload: () => Promise<void>;
};

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || 'Request failed';
}

function emptyFormScalars(): MediaFormScalars {
  return { uchharan: '', audio: '', video: '', photo: '', link: '' };
}

function rowToFormScalars(m: DictionaryMedia): MediaFormScalars {
  return {
    uchharan: m.uchharan,
    audio: m.audio ?? '',
    video: m.video ?? '',
    photo: m.photo ?? '',
    link: m.link ?? '',
  };
}

export function EntryMediaPanel({ variant, parentId, rawMedia, onReload }: EntryMediaPanelProps) {
  const [row, setRow] = useState<DictionaryMedia | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [upsertForm] = Form.useForm<MediaFormScalars>();
  const [editForm] = Form.useForm<MediaFormScalars>();

  const audioRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  const embedded = useMemo(
    () => parseMediaForEntry(rawMedia, variant, parentId),
    [rawMedia, variant, parentId]
  );

  const sync = useCallback(async () => {
    if (embedded) {
      setRow(embedded);
      return;
    }
    setLoading(true);
    try {
      const list = await listMedia(
        variant === 'subanta' ? { subantaId: parentId } : { tingantaId: parentId }
      );
      setRow(list[0] ?? null);
    } catch (err: unknown) {
      message.error(errMessage(err) || 'Failed to load media');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [embedded, variant, parentId]);

  useEffect(() => {
    void sync();
  }, [sync]);

  const afterMutation = useCallback(async () => {
    await onReload();
    await sync();
  }, [onReload, sync]);

  const parentArg =
    variant === 'subanta' ? { subantaId: parentId } : { tingantaId: parentId };

  const submitUpsert = async () => {
    try {
      const v = await upsertForm.validateFields();
      const u = v.uchharan.trim();
      if (!u) {
        message.error('Uchharan is required');
        return;
      }
      setBusy(true);
      await createMedia({
        ...parentArg,
        uchharan: u,
        audio: v.audio?.trim() || undefined,
        video: v.video?.trim() || undefined,
        photo: v.photo?.trim() || undefined,
        link: v.link?.trim() || undefined,
      });
      message.success('Media saved');
      upsertForm.resetFields();
      await afterMutation();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    if (!row) return;
    editForm.setFieldsValue(rowToFormScalars(row));
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!row) return;
    try {
      const v = await editForm.validateFields();
      const patch = buildMediaPatchFromForm(row, v);
      if (Object.keys(patch).length === 0) {
        message.info('No changes');
        setEditOpen(false);
        return;
      }
      setBusy(true);
      await updateMedia(row.id, patch);
      message.success('Media updated');
      setEditOpen(false);
      await afterMutation();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!row) return;
    setBusy(true);
    try {
      await deleteMedia(row.id);
      message.success('Media deleted');
      await afterMutation();
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const uchharanForUpload = (): string | null => {
    const fromRow = row?.uchharan?.trim();
    if (fromRow) return fromRow;
    try {
      return upsertForm.getFieldValue('uchharan')?.trim() || null;
    } catch {
      return null;
    }
  };

  const runUpload = async (
    file: File,
    kind: 'audio' | 'video' | 'photo'
  ): Promise<void> => {
    const uchharan = uchharanForUpload();
    if (!uchharan) {
      message.warning('Enter uchharan in the form first (pronunciation text).');
      return;
    }
    setBusy(true);
    try {
      const args = { ...parentArg, file, uchharan };
      if (kind === 'audio') await uploadMediaAudio(args);
      else if (kind === 'video') await uploadMediaVideo(args);
      else await uploadMediaPhoto(args);
      message.success('File uploaded');
      await afterMutation();
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onPickFile =
    (kind: 'audio' | 'video' | 'photo') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = '';
      if (f) void runUpload(f, kind);
    };

  const mediaFields = (
    <>
      <Form.Item
        name="uchharan"
        label="Uchharan (pronunciation)"
        rules={[{ required: true, message: 'Uchharan is required' }]}
      >
        <Input placeholder="e.g. pronunciation guide" />
      </Form.Item>
      <Form.Item name="audio" label="Audio path/URL (optional)">
        <Input placeholder="/uploads/audio/… or full URL" />
      </Form.Item>
      <Form.Item name="video" label="Video path/URL (optional)">
        <Input placeholder="/uploads/video/… or full URL" />
      </Form.Item>
      <Form.Item name="photo" label="Photo path/URL (optional)">
        <Input placeholder="/uploads/photo/… or full URL" />
      </Form.Item>
      <Form.Item name="link" label="Link (optional)">
        <Input placeholder="https://…" />
      </Form.Item>
    </>
  );

  const audioSrc = resolveMediaAssetUrl(row?.audio);
  const videoSrc = resolveMediaAssetUrl(row?.video);
  const photoSrc = resolveMediaAssetUrl(row?.photo);
  const linkHref = row?.link?.trim() || null;

  return (
    <div style={{ marginTop: 16 }}>
      <Typography.Title level={5} style={{ marginBottom: 8 }}>
        Media
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        At most one row per entry; <code>POST /media</code> upserts. Uploads use{' '}
        <code>/media/upload/…</code> (requires uchharan).
      </Typography.Paragraph>

      {loading ? (
        <Typography.Text type="secondary">Loading…</Typography.Text>
      ) : row ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="ID">{row.id}</Descriptions.Item>
            <Descriptions.Item label="Uchharan">{row.uchharan || '—'}</Descriptions.Item>
            <Descriptions.Item label="Audio path">{row.audio || '—'}</Descriptions.Item>
            <Descriptions.Item label="Video path">{row.video || '—'}</Descriptions.Item>
            <Descriptions.Item label="Photo path">{row.photo || '—'}</Descriptions.Item>
            <Descriptions.Item label="Link">{row.link || '—'}</Descriptions.Item>
          </Descriptions>

          {audioSrc ? (
            <div>
              <Typography.Text strong>Audio</Typography.Text>
              <audio controls src={audioSrc} style={{ display: 'block', width: '100%', marginTop: 8 }} />
            </div>
          ) : null}
          {videoSrc ? (
            <div>
              <Typography.Text strong>Video</Typography.Text>
              <video
                controls
                src={videoSrc}
                style={{ display: 'block', width: '100%', maxHeight: 320, marginTop: 8 }}
              />
            </div>
          ) : null}
          {photoSrc ? (
            <div>
              <Typography.Text strong>Photo</Typography.Text>
              <img
                alt="Media"
                src={photoSrc}
                style={{ maxWidth: '100%', maxHeight: 280, marginTop: 8, borderRadius: 8 }}
              />
            </div>
          ) : null}
          {linkHref ? (
            <Typography.Link href={linkHref} target="_blank" rel="noreferrer">
              <LinkOutlined /> {linkHref}
            </Typography.Link>
          ) : null}

          <input
            type="file"
            ref={audioRef}
            accept=".mp3,.wav,.m4a,audio/*"
            style={{ display: 'none' }}
            onChange={onPickFile('audio')}
          />
          <input
            type="file"
            ref={videoRef}
            accept=".mp4,.webm,.ogv,.mov,video/*"
            style={{ display: 'none' }}
            onChange={onPickFile('video')}
          />
          <input
            type="file"
            ref={photoRef}
            accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
            style={{ display: 'none' }}
            onChange={onPickFile('photo')}
          />

          <Space wrap>
            <Button icon={<EditOutlined />} onClick={openEdit} loading={busy}>
              Edit fields
            </Button>
            <Button
              icon={<SoundOutlined />}
              onClick={() => audioRef.current?.click()}
              loading={busy}
            >
              Upload audio
            </Button>
            <Button
              icon={<VideoCameraOutlined />}
              onClick={() => videoRef.current?.click()}
              loading={busy}
            >
              Upload video
            </Button>
            <Button
              icon={<FileImageOutlined />}
              onClick={() => photoRef.current?.click()}
              loading={busy}
            >
              Upload photo
            </Button>
            <Popconfirm title="Delete this media row?" onConfirm={() => void handleDelete()}>
              <Button danger icon={<DeleteOutlined />} loading={busy}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Typography.Text type="secondary">No media yet — create or upload below.</Typography.Text>
          <Form
            form={upsertForm}
            layout="vertical"
            preserve={false}
            initialValues={emptyFormScalars()}
          >
            {mediaFields}
            <Space wrap>
              <Button type="primary" onClick={() => void submitUpsert()} loading={busy}>
                Save media (upsert)
              </Button>
              <Button
                icon={<SoundOutlined />}
                onClick={() => audioRef.current?.click()}
                loading={busy}
              >
                Upload audio
              </Button>
              <Button
                icon={<VideoCameraOutlined />}
                onClick={() => videoRef.current?.click()}
                loading={busy}
              >
                Upload video
              </Button>
              <Button
                icon={<FileImageOutlined />}
                onClick={() => photoRef.current?.click()}
                loading={busy}
              >
                Upload photo
              </Button>
            </Space>
          </Form>
          <input
            type="file"
            ref={audioRef}
            accept=".mp3,.wav,.m4a,audio/*"
            style={{ display: 'none' }}
            onChange={onPickFile('audio')}
          />
          <input
            type="file"
            ref={videoRef}
            accept=".mp4,.webm,.ogv,.mov,video/*"
            style={{ display: 'none' }}
            onChange={onPickFile('video')}
          />
          <input
            type="file"
            ref={photoRef}
            accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
            style={{ display: 'none' }}
            onChange={onPickFile('photo')}
          />
        </Space>
      )}

      <Modal
        title="Edit media"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          editForm.resetFields();
        }}
        onOk={() => void submitEdit()}
        confirmLoading={busy}
        destroyOnClose
        width={560}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          {mediaFields}
        </Form>
      </Modal>
    </div>
  );
}
